import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { RxCross2 } from "react-icons/rx";

import { CardCategoryEnum, CollectionItemConditionEnum } from "@sorcery/db";
import {
  Button,
  H3,
  InternalLink,
  Line,
  P,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@sorcery/ui";

import { CardGalleryImage } from "~/components/molecules/card/CardGalleryImage";
import { CollectionQuantitySelector } from "~/components/molecules/collection/CollectionQuantitySelector";
import { useModal } from "~/providers/ModalProvider";
import { fullCollectionlistJotai } from "~/state/collectionJotai";
import type { CardType, VariantType } from "~/types/card";
import { conditionOptions } from "~/utils/filters";
import { cardPath } from "~/utils/paths";
import { api } from "~/utils/trpc";
import { VariantLabel } from "../atoms/VariantLabel";
import { CardFlipper } from "../molecules/card/CardFlipper";

export const CollectionAddGallery = ({
  card,
  variant,
}: {
  card: CardType;
  variant: VariantType;
}) => {
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const fullCollectionlist = useAtomValue(fullCollectionlistJotai)!;
  const [error, setError] = useState("");
  const [legalQuantityError, setLegalQuantityError] = useState(false);
  const { closeModal } = useModal();
  const [condition, setCondition] = useState<CollectionItemConditionEnum>(
    CollectionItemConditionEnum.NM,
  );
  const [bulkQ, setBulkQ] = useState(1);

  const hasReverse = !!card.reverse;
  const [isReversed, setIsReversed] = useState(false);

  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const isSite = card.guardian!.category === CardCategoryEnum.Site;

  const toggleVariant = () => {
    setIsReversed(!isReversed);
  };
  const displayedVariant =
    isReversed && hasReverse
      ? // the no reverse on a reverse issue
        (variant.reverse as unknown as VariantType)
      : variant;

  const itemsInMyCollection =
    fullCollectionlist
      .find((i) => i.card.id === card.id)
      ?.groupings.find((g) => g.variantId === variant.id)?.items ?? [];

  const trpcUtils = api.useUtils();

  const {
    mutateAsync: bulkAdd,
    error: updateError,
    isPending,
  } = api.collection.bulkAdd.useMutation({
    onSuccess: () => {
      toast("Collection updated");
      void trpcUtils.collection.getCollectionlistBySession.invalidate();
      void trpcUtils.collection.search.invalidate();
      closeModal();
    },
  });

  // Track any trpc errors for display
  useEffect(() => {
    if (updateError) {
      // don't expose server side error messages
      setError("Something went wrong");
    }
  }, [updateError]);

  const onBulkAdd = () => {
    // clear errs
    setError("");
    setLegalQuantityError(false);

    if (bulkQ < 1 || Number.isNaN(bulkQ)) return setLegalQuantityError(true);

    void bulkAdd({
      cards: [
        {
          cardId: card.id,
          variants: [
            {
              variantId: variant.id,
              quantity: bulkQ,
            },
          ],
        },
      ],
      condition,
    });
  };

  return (
    <div className="relative w-fit">
      {/**
       * headlessui focuses first focusable element,
       * so using a button here with outline-none is our hack to control that
       */}
      <button
        type="button"
        onClick={() => closeModal()}
        className={`absolute right-0 top-0 cursor-pointer text-3xl 
          text-white outline-none sm:-right-1 sm:-top-8 md:top-0`}
      >
        <RxCross2 />
      </button>

      {/* content */}
      <div className="flex flex-col items-center justify-center sm:flex-row sm:items-start">
        {/* image */}
        <div className="relative mb-2 hidden sm:block">
          <CardGalleryImage variant={variant} rotate={isSite} />
          {hasReverse && <CardFlipper onFlip={toggleVariant} />}
        </div>

        {/* right section */}
        <div className="gallery-breakpoints sm:ml-6 ">
          <div className="hidden sm:!block">
            <H3 className="!mb-2">{card.name}</H3>
            <div className="duration-500 animate-in fade-in">
              {/* biome-ignore lint/style/noNonNullAssertion: <explanation> */}
              <P className="!mb-2 italic">{displayedVariant.vMeta!.typeText}</P>
              <P className="curiosa-quote !mb-2 opacity-80">
                {/* biome-ignore lint/style/noNonNullAssertion: <explanation> */}
                {displayedVariant.setCard.meta!.rulesText}
              </P>
              <P className="opacity-80">{displayedVariant.artist.name}</P>
            </div>
          </div>

          {/* sm screen */}
          <H3 className="text-center sm:hidden">{card.name}</H3>
          <div
            // biome-ignore lint/style/noUnusedTemplateLiteral: <explanation>
            className={`relative mb-8 flex flex-row justify-center duration-500 animate-in fade-in sm:mb-0 sm:hidden`}
            key={displayedVariant.id}
          >
            <CardGalleryImage variant={displayedVariant} rotate={isSite} />
            {/* reverse flipper */}
            {hasReverse && <CardFlipper onFlip={toggleVariant} />}
          </div>
          <Line className="my-4 !bg-line-color" />

          <div className="-ml-2 flex items-center">
            {/* variant identifier */}
            <VariantLabel variant={displayedVariant} />
          </div>

          {/* collection stuff */}
          <P className="!mb-0 text-left">
            <span>{itemsInMyCollection.length ? itemsInMyCollection.length : "None"}</span>{" "}
            <span>in your Collection</span>
          </P>

          <Line className="my-4 !bg-line-color" />

          {/* edit options */}
          <div className="flex">
            <div className="mr-2">
              <P className="!mb-2 text-left">Bulk Add</P>
              <CollectionQuantitySelector
                quantity={bulkQ}
                onChange={setBulkQ}
                error={legalQuantityError}
              />
            </div>
            <div className="mr-2 w-[5rem]">
              <P className="!mb-2 text-left">Condition</P>

              <Select onValueChange={(v) => setCondition(v as CollectionItemConditionEnum)}>
                <SelectTrigger arrow>
                  <SelectValue
                    placeholder={
                      // biome-ignore lint/style/noNonNullAssertion: <explanation>
                      conditionOptions.find((d) => d.value === condition)!.label
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {conditionOptions.map((d) => (
                    <SelectItem key={d.label} value={d.value}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* error */}
          {error && (
            <div className="text-red my-4" role="alert">
              <p>
                <span className="font-bold">Error. </span>
                {error}
                <span>.</span>
              </p>
            </div>
          )}
        </div>
      </div>

      {/* footer */}
      <Line className="my-4 !bg-line-color" />
      <div className="flex flex-row-reverse justify-between">
        <div className="flex flex-row-reverse gap-2">
          <Button onClick={onBulkAdd} disabled={isPending} className="min-w-[5rem]">
            {isPending ? <>Adding...</> : <>Add</>}
          </Button>

          <Button onClick={closeModal} variant="outline">
            Cancel
          </Button>
        </div>
        <InternalLink
          href={cardPath(card.slug)}
          onClick={() => closeModal()}
          className="hover:text-primary cursor-pointer transition-colors"
        >
          Go to card
        </InternalLink>
      </div>
    </div>
  );
};
